<template>
  <b-modal
    id="add-or-update-event-center"
    :visible="isAddOrUpdateActive"
    centered
    hide-footer
    hide-header
    no-close-on-backdrop
    no-close-on-esc
    @hidden="resetForm"
    @hide="resetForm"
    @change="(val) => $emit('update:is-add-or-update-active', val)"
  >
    <template #default="{ hide }">
      <b-overlay :show="loading" class="px-2">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header py-1" style="border-bottom: 1px solid #000000;">
          <h5 class="mb-0" style="font-weight: 400; font-size: 14px; line-height: 160%;">
            {{ isUpdate ? 'Update' : 'Add New' }}
          </h5>

          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>

        <validation-observer
          #default="{ handleSubmit }"
          ref="update_modal_form"
        >
          <b-form
            class="my-1"
            @submit.prevent="handleSubmit(onCreateOrUpdate)"
            @reset.prevent="resetForm"
          >

            <b-alert
              v-if="errorMessage"
              variant="danger"
              :show="true"
            >
              <div class="alert-body font-small-2">
                <b-card-text class="mb-0">
                  {{ errorMessage }}
                </b-card-text>
              </div>
            </b-alert>
            
            <validation-provider
              #default="validationContext"
              name="Institution"
              rules="required"
            >
              <b-form-group
                label="Institution"
                label-for="name_of_institution"
              >
                <template #label>
                  <span>Institution<sup style="color: tomato">*</sup></span>
                </template>

                <b-form-input
                  id="name_of_institution"
                  v-model="new_data.name_of_institution"
                  autofocus
                  trim
                  placeholder="Ex: University Of Ghana"
                  :disabled="disableForm"
                  :state="getValidationState(validationContext)"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="validationContext"
              name="Type of certificate"
              rules="required"
            >
              <b-form-group
                label="Type of certificate"
                label-for="type_of_certificate"
              >
                <template #label>
                  <span>Type of certificate<sup style="color: tomato">*</sup></span>
                </template>

                <b-form-select
                  id="type_of_certificate"
                  v-model="new_data.type_of_certificate"
                  :options="type_of_certificates"
                  placeholder="Please select"
                  :disabled="disableForm"
                  :state="getValidationState(validationContext)"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="validationContext"
              name="Program studied"
              rules="required"
            >
              <b-form-group
                label="Program studied"
                label-for="program_studied"
              >
                <template #label>
                  <span>Program studied<sup style="color: tomato">*</sup></span>
                </template>

                <b-form-input
                  id="program_studied"
                  v-model="new_data.program_studied"
                  trim
                  placeholder="Ex: Bsc Information Technology"
                  :disabled="disableForm"
                  :state="getValidationState(validationContext)"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              #default="validationContext"
              name="Grade or qualification"
              rules=""
            >
              <b-form-group
                label="Grade or qualification"
                label-for="grade_or_qualification"
              >
                <b-form-input
                  id="grade_or_qualification"
                  v-model="new_data.grade_or_qualification"
                  trim
                  placeholder=""
                  :disabled="disableForm"
                  :state="getValidationState(validationContext)"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <b-form-group label="I am still schooling here" class="mb-2">
              <validation-provider
                #default="{ errors }"
                name="is_cop_area_head"
                rules=""
              >
                <b-form-checkbox
                  v-model="new_data.still_schooling"
                  :state="errors.length > 0 ? false : null"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <validation-provider
              #default="validationContext"
              name="Start date"
              rules="required"
            >
              <b-form-group
                label="Start date"
                label-for="start_date"
              >
                <template #label>
                  <span>Start date<sup style="color: tomato">*</sup></span>
                </template>

                <b-form-datepicker
                  id="date_started"
                  v-model="new_data.date_started"
                  value-as-date
                  :disabled="disableForm"
                  :state="getValidationState(validationContext)"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider
              v-if="!new_data.still_schooling"
              #default="validationContext"
              name="Start date"
              rules="required"
            >
              <b-form-group
                label="Start date"
                label-for="date_ended"
              >
                <template #label>
                  <span>End date<sup style="color: tomato">*</sup></span>
                </template>

                <b-form-datepicker
                  id="date_ended"
                  v-model="new_data.date_ended"
                  :min="new_data.date_started"
                  value-as-date
                  :disabled="disableForm"
                  :state="getValidationState(validationContext)"
                />

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
                :disabled="disableForm"
              >
                {{ isUpdate ? 'Update' : 'Add' }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-modal>
</template>

<script>
import {
  BOverlay, BSidebar, BForm, BFormGroup, BFormInput, 
  BFormTextarea, BFormInvalidFeedback, BButton, BFormSelect,
  BAlert, BCardText, BModal, BFormCheckbox, BFormDatepicker,
  BFormFile
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import { get, cloneDeep, omit, uniqWith } from 'lodash';
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { addDays, addYears, isAfter, addMonths, endOfMonth } from 'date-fns/esm';

import { required } from '@validations'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'

const rawFormData = {
  name_of_institution: "",
  type_of_certificate: "",
  program_studied: "",
  grade_or_qualification: "",
  date_started: "",
  date_ended: ""
}

export default {
  components: {
    BForm,
    BAlert,
    BModal,
    BButton,
    vSelect,
    BOverlay,
    BSidebar,
    BCardText,
    BFormFile,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormCheckbox,
    BFormDatepicker,
    BFormInvalidFeedback,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddOrUpdateActive',
    event: 'update:is-add-or-update-active',
  },
  props: {
    isAddOrUpdateActive: {
      type: Boolean,
      required: true,
    },
    updateData: {
      type: Object,
      default: null,
      required: false
    },
    userId: {
      type: String,
      required: true
    },
    isPersonal: {
      type: Boolean,
      required: true
    },
  },
  data() {
    return {
      required,
      loading: false,
      errorMessage: "",
      type_of_certificates: [
        "Other",
        "Master's Degree",
        "Bachelor's Degree",
        "Associate's Degree",
        "High School Diploma",
        "Post Graduate Diploma",
        "Professional Certificate",
        "Trade School Certificate",
        "Technical School Certificate",
        "Professional Licensure/Certification",
        "Doctorate Degree  (Ph.D., Ed.D, etc)",
        "General Education Development (GED) Certificate",
      ].map(type => ({ text: type, value: type })),

      new_data: cloneDeep(rawFormData),
      attachments: [],
      editing_attachments: []
    }
  },
  computed: {
    isUpdate(){
      return !!this.updateData
    },
    disableForm(){
      return false
    },
  },
  watch: {
    updateData: {
      handler(updateData){
        if (updateData){
          this.new_data = {
            _id: updateData._id,
            name_of_institution: updateData.name_of_institution,
            type_of_certificate: updateData.type_of_certificate,
            program_studied: updateData.program_studied,
            grade_or_qualification: updateData.grade_or_qualification,
            date_started: updateData.date_started,
            date_ended: updateData.date_ended,
            still_schooling: updateData.still_schooling,
          }
          this.editing_attachments = updateData.attachments || []
        }
      },
      immediate: true,
      deep: true
    },
    'new_data.date_started': {
      handler(date_started){
        const { date_ended } = this.new_data;
        if (date_ended){
          if (isAfter(new Date(date_started), new Date(date_ended))){
            this.new_data.date_ended = addYears(addDays(new Date(date_started), 1), 1)
          }
        } else {
          this.new_data.date_ended = endOfMonth(addMonths(new Date(date_started), 1))
        }
      }
    }
  },
  methods: {
    async onCreateOrUpdate(){
      try {
        this.loading = true;
        this.errorMessage = "";

        const success = await this.$refs.update_modal_form.validate();
        if (!success){
          return;
        }

        const messages = {
          update: {
            title: "Confirm Update",
            text: "Proceed to update education history?",
            icon: "warning"
          },
          new: {
            title: "Create",
            text: "Proceed to update education history?",
            icon: "info"
          }
        }

        /* eslint-disable no-nested-ternary */
        const message = this.isUpdate ? messages.update : messages.new;
        const { title, text, icon } = message;

        const result = await this.$swal({
          title,
          text,
          icon,
          showCancelButton: true,
          confirmButtonText: 'Yes, proceed.',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        });
        if (!result.isConfirmed) {
          return;
        }
        
        const update_data = omit(this.new_data, ["_id", "attachments"]);
        const id = this.new_data._id

        const formData = new FormData()

        Object.keys(update_data).forEach((field) => {
          formData.append(field, update_data[field])
        })
        this.attachments.forEach((attachment) => {
          formData.append(`attachments`, attachment)
        })

        const apiService = this.isPersonal ? this.useJwt().mainService.userEducation : this.useJwt().mainService.education;
        if (this.isUpdate){
          await apiService.update(id, formData);
        } else {
          if (!this.isPersonal) {
            formData.append("user_id", this.userId)
          }
          await apiService.create(formData);
        }
        this.$emit("refetchData");
        this.$emit('update:is-add-or-update-active', false);
        this.attachments = []

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'AlertQuestionIcon',
            variant: 'success',
            text: `${this.isUpdate ? 'Updated' : 'Added'} successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    addAttachmentFile() {
      const { attachmentUploader } = this.$refs
      if (attachmentUploader && attachmentUploader.$el) {
        attachmentUploader.$el.childNodes[0].click()
      }
    },
    fileSelected(fileOrFiles) {
      if (Array.isArray(fileOrFiles)) {
        this.attachments = uniqWith([...this.attachments, ...fileOrFiles], (a, b) => a.name === b.name)
      }
    },
    resetForm() {
      this.$emit("resetUpdateData")
      this.new_data = {};
      if (this.$refs.update_modal_form) {
        this.$refs.update_modal_form.reset()
      }
    },
    removeFileFromIndex(index) {
      this.attachments.splice(index, 1)
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-or-update-event-center {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}

</style>
